import React from "react";
import s from "./ScoresReport.module.scss";
import ScoreItem from "../ScoreItem";
import { ReportRiskValue } from "./ReportRiskValue";
import { ReportCTA } from "./ReportCTA";
import {
  findPlaceFromQuery,
  fetchScores,
} from "../../services/placeSearchService";
import {
  adaptDataApiRatings,
  formatScore,
  formatSortScores,
} from "../../utils/utils";
import { Score, scores_types } from "../../utils/types";
import LoadingElement from "../LoadingElement";

type State = {
  address: string;
  message: string;
  scores: Score[];
};

export const ScoresReport: React.FC<{ address: string; apiKey: string }> = ({
  address,
  apiKey,
}) => {
  const [state, setState] = React.useState<State>({
    address,
    scores: [],
    message: "Fetching Location...",
  });

  const [climateScore, setClimateScore] = React.useState<Score>();

  const updateMessage = (message: string) => setState({ ...state, message });

  React.useEffect(() => {
    // Find the Place by address
    findPlaceFromQuery(address)
      .then(async (data) => {
        if (typeof data === "string") {
          updateMessage(data);
          return;
        }

        const { place_name, geometry } = data;

        const [lng, lat] = geometry.coordinates;

        if (!lat || !lng) {
          updateMessage(
            "Missing location fields, cannot calculate score, please try another address"
          );
          return;
        }

        if (!apiKey) {
          updateMessage(
            "This widget is missing its access key. Please contact the site owner."
          );
          return;
        }

        const result = await fetchScores(lat, lng, apiKey);
        if (!result.ok) {
          if (result.status === 429) {
            updateMessage(
              "This widget has reached its request limit. Please try again later."
            );
          } else if (result.status === 401 || result.status === 403) {
            updateMessage(
              "This widget access key is invalid. Please contact the site owner."
            );
          } else {
            updateMessage(
              "We could not calculate this location right now. Please try again later."
            );
          }
          return;
        }

        const response = await result?.json();
        const { ClimateRisk, ...scoresValues } = adaptDataApiRatings(response);

        const cs = formatScore(
          scores_types.ClimateRisk,
          response[scores_types.ClimateRisk]
        );

        setClimateScore(cs);

        const formattedScores = formatSortScores(scoresValues);

        setState({
          address: place_name || "",
          scores: formattedScores,
          message: "",
        });
      })
      .catch((e) => {
        updateMessage(
          "An unkown error ocurred, please contact climatecheck.com"
        );
      });
    // eslint-disable-next-line
  }, [apiKey, address]);

  if (state.message) {
    return <LoadingElement text={state.message} />;
  }

  const [first] = state.scores;
  const last = state.scores[state.scores.length - 1];

  return (
    <>
      <div className={s.widgetOverline}>
        <p className={s.widgetOverlineTitle}>
          How will climate change affect your home?
        </p>
        <p className={s.widgetOverlineAddress}>{address}</p>
      </div>
      <ReportRiskValue score={climateScore} first={first} last={last} />
      <div className={s.widgetDivider}></div>
      <div className={s.widgetScores}>
        <ul>
          {state.scores.map((score, i) => (
            <ScoreItem score={score} key={i} />
          ))}
        </ul>
      </div>
      <ReportCTA address={address} />
    </>
  );
};
